class CasesFilterControls {
    constructor(root) {
        this.root = root;
        this.content = document.querySelector('#cases .hero-content');
        this.currentRequest = null;
        this.responseCache = new Map();
        this.prefetchRequests = new Map();

        this.refreshElements();
        this.normalizeStaticLinks();
        this.bindEvents();
        this.clearLoadingState();
        this.scheduleCategoryControlsUpdate();
        this.syncActiveState(window.location.href);
    }

    refreshElements() {
        this.categories = this.root.querySelector('.cases-filter__categories');
        this.prevButton = this.root.querySelector('.cases-filter__categories-nav--prev');
        this.nextButton = this.root.querySelector('.cases-filter__categories-nav--next');
        this.dropdown = this.root.querySelector('.cases-filter__dropdown');
        this.dropdownButton = this.root.querySelector('.cases-filter__dropdown-button');
        this.summaryText = this.root.querySelector('.cases-filter__summary-text');
    }

    bindEvents() {
        this.root.addEventListener('click', (event) => {
            const link = event.target.closest('a');

            if (!link || !this.root.contains(link)) {
                return;
            }

            this.handleFilterClick(event, link);
        });

        this.root.addEventListener('mouseover', (event) => {
            const link = event.target.closest('a');

            if (link && this.root.contains(link)) {
                this.prefetchCases(link.href);
            }
        });

        this.root.addEventListener('focusin', (event) => {
            const link = event.target.closest('a');

            if (link && this.root.contains(link)) {
                this.prefetchCases(link.href);
            }
        });

        this.root.addEventListener('touchstart', (event) => {
            const link = event.target.closest('a');

            if (link && this.root.contains(link)) {
                this.prefetchCases(link.href);
            }
        }, { passive: true });

        document.addEventListener('click', (event) => {
            const paginationLink = event.target.closest('#cases .cases-list__pagination a');

            if (paginationLink) {
                this.handleFilterClick(event, paginationLink);
                return;
            }

            if (!this.dropdown || this.root.contains(event.target)) {
                return;
            }

            this.closeDropdown();
        });

        if (this.dropdown && this.dropdownButton) {
            this.root.addEventListener('click', (event) => {
                const dropdownButton = event.target.closest('.cases-filter__dropdown-button');

                if (!dropdownButton || !this.root.contains(dropdownButton)) {
                    return;
                }

                this.toggleDropdown();
            });
        }

        this.root.addEventListener('click', (event) => {
            const prevButton = event.target.closest('.cases-filter__categories-nav--prev');
            const nextButton = event.target.closest('.cases-filter__categories-nav--next');

            if (prevButton && this.root.contains(prevButton)) {
                this.scrollCategories(-1);
                return;
            }

            if (nextButton && this.root.contains(nextButton)) {
                this.scrollCategories(1);
            }
        });

        if (this.categories) {
            this.root.addEventListener('scroll', (event) => {
                if (event.target === this.categories) {
                    this.updateCategoryControls();
                }
            }, true);

            window.addEventListener('resize', () => this.updateCategoryControls());
            window.addEventListener('pageshow', () => {
                this.refreshElements();
                this.normalizeStaticLinks();
                this.clearLoadingState();
                this.syncActiveState(window.location.href);
                this.scheduleCategoryControlsUpdate();
            });
        }

        window.addEventListener('popstate', () => {
            this.loadCases(window.location.href, false);
        });

        document.addEventListener('keydown', (event) => {
            if ('Escape' === event.key) {
                this.closeDropdown();
            }
        });
    }

    handleFilterClick(event, link) {
        if (link.classList.contains('disabled') || link.getAttribute('aria-disabled') === 'true') {
            event.preventDefault();
            return;
        }

        const targetUrl = link.href;

        if (!targetUrl) {
            return;
        }

        if (!this.isAjaxFilterUrl(targetUrl)) {
            return;
        }

        event.preventDefault();
        this.loadCases(targetUrl, true);
    }

    toggleDropdown() {
        if (this.dropdown.classList.contains('is-open')) {
            this.closeDropdown();
            return;
        }

        this.dropdown.classList.add('is-open');
        this.dropdownButton.setAttribute('aria-expanded', 'true');
    }

    closeDropdown() {
        if (!this.dropdown || !this.dropdownButton) {
            return;
        }

        this.dropdown.classList.remove('is-open');
        this.dropdownButton.setAttribute('aria-expanded', 'false');
    }

    scrollCategories(direction) {
        this.categories.scrollBy({
            left: direction * Math.round(this.categories.clientWidth * 0.7),
            behavior: 'smooth',
        });
    }

    updateCategoryControls() {
        if (!this.categories || !this.prevButton || !this.nextButton) {
            return;
        }

        const maxScroll = this.categories.scrollWidth - this.categories.clientWidth;
        const hasScroll = maxScroll > 2;
        const shell = this.categories.closest('.cases-filter__categories-shell');

        if (shell) {
            shell.classList.toggle('has-scroll', hasScroll);
        }

        this.prevButton.disabled = !hasScroll || this.categories.scrollLeft <= 2;
        this.nextButton.disabled = !hasScroll || this.categories.scrollLeft >= maxScroll - 2;
    }

    scheduleCategoryControlsUpdate() {
        this.updateCategoryControls();

        window.requestAnimationFrame(() => {
            this.updateCategoryControls();

            window.requestAnimationFrame(() => this.updateCategoryControls());
        });

        window.setTimeout(() => this.updateCategoryControls(), 120);
    }

    loadCases(targetUrl, shouldPushState) {
        const cacheKey = this.getCacheKey(targetUrl);
        const cachedResponse = this.responseCache.get(cacheKey);
        const ajaxUrl = this.getAjaxUrl(targetUrl);

        if (!ajaxUrl) {
            window.location.href = targetUrl;
            return;
        }

        if (this.currentRequest) {
            this.currentRequest.abort();
        }

        if (cachedResponse) {
            this.currentRequest = null;
            this.applyResponse(cachedResponse, targetUrl, shouldPushState);
            this.clearLoadingState();
            this.scheduleCategoryControlsUpdate();
            return;
        }

        const requestController = new AbortController();

        this.currentRequest = requestController;
        this.setLoadingState();
        this.closeDropdown();

        this.fetchCases(targetUrl, requestController.signal)
            .then((response) => {
                this.applyResponse(response, targetUrl, shouldPushState);
            })
            .catch((error) => {
                if ('AbortError' === error.name) {
                    return;
                }

                window.location.href = targetUrl;
            })
            .finally(() => {
                if (this.currentRequest !== requestController) {
                    return;
                }

                this.currentRequest = null;
                this.clearLoadingState();
                this.scheduleCategoryControlsUpdate();
            });
    }

    prefetchCases(targetUrl) {
        if (!this.isAjaxFilterUrl(targetUrl)) {
            return;
        }

        const cacheKey = this.getCacheKey(targetUrl);

        if (this.responseCache.has(cacheKey) || this.prefetchRequests.has(cacheKey)) {
            return;
        }

        const ajaxUrl = this.getAjaxUrl(targetUrl);

        if (!ajaxUrl) {
            return;
        }

        const request = this.fetchCases(targetUrl)
            .catch(() => null)
            .finally(() => {
                this.prefetchRequests.delete(cacheKey);
            });

        this.prefetchRequests.set(cacheKey, request);
    }

    fetchCases(targetUrl, signal = null) {
        const cacheKey = this.getCacheKey(targetUrl);
        const prefetchRequest = this.prefetchRequests.get(cacheKey);

        if (prefetchRequest) {
            return prefetchRequest;
        }

        const ajaxUrl = this.getAjaxUrl(targetUrl);

        return fetch(ajaxUrl.toString(), {
            method: 'GET',
            credentials: 'same-origin',
            signal,
        })
            .then((response) => response.json())
            .then((response) => {
                if (!response || !response.success || !response.data || typeof response.data.html !== 'string') {
                    throw new Error('Invalid cases response');
                }

                this.responseCache.set(cacheKey, response);

                return response;
            });
    }

    applyResponse(response, targetUrl, shouldPushState) {
        this.replaceFilters(response.data.filtersHtml);

        if (this.content) {
            this.content.innerHTML = response.data.html;
        }

        if (this.summaryText && response.data.countLabel) {
            this.summaryText.textContent = response.data.countLabel;
        }

        this.syncActiveState(targetUrl);

        if (shouldPushState && targetUrl !== window.location.href) {
            window.history.pushState({}, '', targetUrl);
        }
    }

    replaceFilters(filtersHtml) {
        if (!filtersHtml) {
            return;
        }

        const template = document.createElement('template');

        template.innerHTML = filtersHtml.trim();

        const nextRoot = template.content.querySelector('[data-cases-filter]');

        if (!nextRoot) {
            return;
        }

        this.root.className = nextRoot.className;
        this.root.innerHTML = nextRoot.innerHTML;
        this.refreshElements();
        this.normalizeStaticLinks();
        this.scheduleCategoryControlsUpdate();
    }

    getCacheKey(targetUrl) {
        const target = new URL(targetUrl, window.location.href);
        const allowedParams = ['case_category', 'case_year', 'case_sort', 'case_page'];
        const cacheUrl = new URL(target.origin + target.pathname);

        allowedParams.forEach((param) => {
            if (target.searchParams.has(param)) {
                cacheUrl.searchParams.set(param, target.searchParams.get(param));
            }
        });

        return cacheUrl.toString();
    }

    isAjaxFilterUrl(targetUrl) {
        const target = new URL(targetUrl, window.location.href);

        return target.origin === window.location.origin
            && this.normalizePath(target.pathname) === this.normalizePath(window.location.pathname);
    }

    isSmmCaseUrl(targetUrl) {
        const target = new URL(targetUrl, window.location.href);

        return target.origin === window.location.origin && this.normalizePath(target.pathname).endsWith('/cases/smm');
    }

    normalizePath(path) {
        return '/' === path ? path : path.replace(/\/+$/, '');
    }

    normalizeStaticLinks() {
        this.root.querySelectorAll('.cases-filter__category').forEach((link) => {
            if (!this.isSmmCaseUrl(link.href)) {
                return;
            }

            const text = link.querySelector('.cases-filter__category-text');

            link.classList.remove('is-active');

            if (text) {
                text.textContent = 'SMM';
            }
        });
    }

    getCasesBaseUrl() {
        return window.location.origin + window.location.pathname;
    }

    getAjaxUrl(targetUrl) {
        if (!window.custom_vars || !window.custom_vars.ajaxurl || !window.custom_vars.cases_filter_nonce) {
            return null;
        }

        const target = new URL(targetUrl, window.location.href);
        const ajaxUrl = new URL(window.custom_vars.ajaxurl);
        const allowedParams = ['case_category', 'case_year', 'case_sort', 'case_page'];

        ajaxUrl.searchParams.set('action', 'recipe_cases_filter');
        ajaxUrl.searchParams.set('nonce', window.custom_vars.cases_filter_nonce);
        ajaxUrl.searchParams.set('cases_base_url', this.getCasesBaseUrl());

        allowedParams.forEach((param) => {
            if (target.searchParams.has(param)) {
                ajaxUrl.searchParams.set(param, target.searchParams.get(param));
            }
        });

        return ajaxUrl;
    }

    syncActiveState(targetUrl) {
        const target = new URL(targetUrl, window.location.href);
        const activeCategory = target.searchParams.get('case_category') || '';
        const activeYear = target.searchParams.get('case_year') || 'all';
        const activeSort = target.searchParams.get('case_sort') || 'newest';

        this.root.querySelectorAll('.cases-filter__category').forEach((link) => {
            if (!this.isAjaxFilterUrl(link.href)) {
                link.classList.remove('is-active');
                return;
            }

            const linkUrl = new URL(link.href, window.location.href);
            const linkCategory = linkUrl.searchParams.get('case_category') || '';

            link.classList.toggle('is-active', linkCategory === activeCategory);
        });

        this.root.querySelectorAll('.cases-filter__year').forEach((link) => {
            const linkUrl = new URL(link.href, window.location.href);
            const linkYear = linkUrl.searchParams.get('case_year') || 'all';

            link.classList.toggle('is-active', linkYear === activeYear);
        });

        this.root.querySelectorAll('.cases-filter__dropdown-link').forEach((link) => {
            const linkUrl = new URL(link.href, window.location.href);
            const linkSort = linkUrl.searchParams.get('case_sort') || 'newest';
            const isActive = linkSort === activeSort;

            link.classList.toggle('is-active', isActive);
            link.setAttribute('aria-selected', isActive ? 'true' : 'false');

            if (isActive) {
                const dropdownText = this.root.querySelector('.cases-filter__dropdown-text');

                if (dropdownText) {
                    dropdownText.textContent = link.textContent.trim();
                }
            }
        });
    }

    setLoadingState() {
        if (this.content) {
            this.content.setAttribute('aria-busy', 'true');
            this.content.classList.add('is-loading');
        }

        this.root.classList.add('is-loading');
    }

    clearLoadingState() {
        if (this.content) {
            this.content.removeAttribute('aria-busy');
            this.content.classList.remove('is-loading');
        }

        this.root.classList.remove('is-loading');
        this.closeDropdown();
    }
}

document.querySelectorAll('[data-cases-filter]').forEach((root) => {
    new CasesFilterControls(root);
});
